'use client';
import React, { useMemo, useState } from 'react';
import { Tableau } from '@/shared/Tableau';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed, userName } from '@/lib/store';
import { fmtDate, estBiologiste } from '@/lib/meta';
import {
  I, Modale, inputStyle, useBusy, Selecteur } from '@/components/ui';
import { LeveyJennings, type Bloc, type Point, CalibrationDepuisCqi } from './Cqi';
import { useAutorisationResponsable, ChampsResponsable } from '@/components/AutorisationResponsable';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
/* ─────────────────── Import CQI depuis le rapport Indiko (PDF) ───────────────
 * Deux appels, jamais un seul : `apercu` ne persiste rien — elle lit le PDF,
 * rapproche les libellés automate avec le référentiel et calcule un verdict
 * RÉEL (même moteur que l'écriture). `valider` écrit ensuite exactement ce que
 * l'opérateur a gardé, avec la date et l'opérateur qu'il a éventuellement
 * corrigés — le PDF ne porte ni l'un ni l'autre de façon fiable.
 *
 * Rien n'est deviné : un contrôle ou un test que le rapport nomme sans qu'un
 * alias le reconnaisse remonte dans « Non reconnu », jamais absorbé en
 * silence — voir `backend/src/cqi-import.ts`.
 *
 * CORRECTIONS (04/09/2026) : l'opérateur peut corriger, ligne par ligne, un
 * niveau ou un test non reconnu — jamais la valeur mesurée elle-même sur une
 * ligne déjà reconnue, qui reste en lecture seule (voir plus bas). Toute
 * correction REND L'APERÇU PÉRIMÉ : la confirmation reste bloquée tant qu'un
 * « Recalculer l'aperçu » n'a pas redemandé un verdict réel au serveur.
 *
 * TABLEAU (04/09/2026) : une ligne PAR NIVEAU, jamais empilées dans la même
 * cellule — c'est ce qui rendait une reprise illisible (3 valeurs de niveau 1
 * collées bout à bout). Le rapprochement lui-même sépare désormais chaque
 * TENTATIVE en son propre passage (`numeroTentative`, voir `cqi-import.ts`) :
 * une reprise partielle (plus de BD1 que de BD2) ne mélange plus deux essais.
 *
 * LOT (05/09/2026) : le lot de contrôle se choisit AVANT l'aperçu, dans une
 * liste déroulante des lots OUVERTS sur l'équipement (même à une seule
 * entrée) — jamais après coup. C'est ce choix qui dit contre quelle carte
 * (et donc quelle cible) chaque point du rapport sera évalué : le confirmer
 * une fois l'aperçu déjà calculé serait trop tard, l'évaluation aurait déjà
 * pu se faire contre la mauvaise carte pendant une transition de lot.
 * `valider` revérifie côté serveur que chaque carte est encore sur ce lot.
 *
 * REPASSE ET VERDICT (05/09/2026) : un analyte à plusieurs tentatives gardées
 * exige un motif de repasse (liste déroulante, stocké en commentaire du
 * passage). Chaque point reçoit un accepté/refusé PROPOSÉ depuis le verdict
 * calculé, que l'opérateur peut inverser — un refus exige un motif (même
 * règle que le rejet a posteriori, `cqi-points.ts`). La carte et son graphe
 * s'ouvrent depuis la liste, en lecture seule, sans quitter l'import.
 * --------------------------------------------------------------------------- */

type Regle = { code: string; gravite: 'alerte' | 'rejet'; niveau: number | null; libelle?: string };
type NiveauApercu = {
  niveau: number; valeur: number; z: number | null; acceptable: boolean | null;
  unite: string; ctrlIdOriginal: string; etatAutomate: string; erreursAutomate: string;
  page: number; ligneIndex: number; corrige: boolean;
};
type LigneReconnue = {
  analyteCode: string; numeroTentative: number; analyteNom: string; carteId: string; periodeId: string;
  /** Simple rappel d'affichage — le lot a déjà été choisi et vérifié AVANT
   *  cet aperçu (`resoudreCarteEtPeriode` l'a confirmé carte par carte). */
  lotControle: string;
  niveaux: NiveauApercu[]; statutPrevu: 'conforme' | 'alerte' | 'rejet'; reglesPrevu: Regle[];
  /** Le motif de repasse que l'AUTOMATE a lui-même imprimé sur la ligne, quand
   *  il ne laisse aucun doute (« Linéarité » / « Linearity » → linéarité de la
   *  courbe). `''` sinon — l'opérateur dit alors pourquoi il a repassé. */
  motifRepasseDeduit?: string;
  /** ⚠️ CE POINT EXISTE DÉJÀ EN BASE — même période, même date, mêmes valeurs
   *  à tous les niveaux (12/09/2026). Servi par l'aperçu, jamais deviné ici.
   *  Sa présence propose la tentative DÉCOCHÉE ; elle ne la refuse pas : une
   *  vraie repasse dont les valeurs se trouvent identiques existe, et c'est
   *  même plutôt bon signe. C'est l'opérateur qui tranche. */
  doublon?: { passageId: string; serie: number | null; importe: boolean; operateur: string } | null;
};
type LigneNonReconnue = {
  motif: string; test?: string; ctrlId?: string; resultat?: string; unite?: string; etatAutomate?: string;
  analyteCode?: string; niveaux?: { niveau: number; valeur: number; ctrlId: string }[];
  /** Porté seulement par `motif === 'controle_banni'` — ce qu'il faut taper à la place. */
  motifDetail?: string;
  /** Absents pour `carte_absente`/`periode_fermee` (groupe, pas une ligne PDF
   *  précise) — pas de correction possible pour ces deux motifs-là. */
  page?: number;
  ligneIndex?: number;
};
type LigneExclue = { motif: string; test: string; ctrlId: string; resultat: string };
type ReponseApercu = {
  equipementCode: string; dateRapport: string; heureRapport: string; operateurPropose: string;
  reconnues: LigneReconnue[]; nonReconnues: LigneNonReconnue[]; exclues: LigneExclue[];
  rejetAffichage: boolean; rejetCalcul: boolean;
  analytesDisponibles: { code: string; nom: string }[];
  niveauxDisponibles: { niveau: number; label: string }[];
  /** Métadonnée du FICHIER (posée par l'automate), pas du texte imprimé —
   *  `null` si absente ou illisible. Sert à alerter si elle diverge de la
   *  date saisie à l'écran : un vieux PDF rechargé par erreur garde sa vraie
   *  date de génération, même si l'opérateur a changé le champ Date. */
  dateCreationFichier: string | null;
  /** « Version du logiciel » imprimée par l'automate — renvoyée même sans
   *  alerte, pour que `valider` la revérifie sans rouvrir le PDF. */
  versionLogicielFichier: string | null;
  /** Non nul quand la version trouvée diverge de celle attendue pour
   *  l'équipement choisi — garde-fou anti-confusion de machine (06/09/2026).
   *  Non dérogeable sauf pour qui porte `cqiDeroger` (voir `attentes`). */
  alerteMachine: { attendue: string; trouvee: string } | null;
  /** Lequel des deux tableaux de l'Indiko a été lu : le rapport DU JOUR, ou un
   *  ARCHIVAGE tiré après coup (09/09/2026). Affiché pour qu'un fichier chargé
   *  à la place de l'autre se voie avant d'écrire quoi que ce soit. */
  gabarit?: 'journalier' | 'archivage';
  /** LES OPÉRATEURS POSSIBLES — l'organigramme, servi par le serveur, celui-là
   *  même qui revérifie le choix à l'enregistrement (09/09/2026). Une saisie
   *  libre produisait deux graphies de la même personne, qu'aucun journal ne
   *  rapproche ensuite. */
  operateursDisponibles?: { key: string; nom: string; fonction: string }[];
};
type Correction = { niveau?: number; analyteCode?: string; valeur?: number };

/** Une ligne du TABLEAU affiché — un seul niveau, jamais plusieurs empilés.
 *  `attemptKey` regroupe les niveaux d'une même tentative : c'est ELLE que la
 *  case à cocher retient, pas le niveau isolé — on garde ou on écarte un
 *  passage entier, jamais un niveau seul. */
type LigneAffichee = {
  attemptKey: string; analyteCode: string; analyteNom: string; numeroTentative: number; nbTentativesAnalyte: number;
  reglesPrevu: Regle[];
  niveau: number; valeur: number; z: number | null; acceptable: boolean | null; unite: string;
  corrige: boolean;
  /** Ce que l'AUTOMATE lui-même a noté (« Linéarité », …) — jamais le
   *  verdict, mais ce qui motive un refus et un motif de repasse proposés
   *  par défaut (06/09/2026). */
  erreursAutomate: string;
};

/** Même liste que `MOTIFS_REPASSE` côté serveur (`cqi-import.ts`) — dupliquée
 *  ici volontairement (3 entrées, jamais lues ailleurs côté écran) plutôt que
 *  d'ajouter un aller-retour réseau pour un vocabulaire aussi court. */
const MOTIFS_REPASSE: Record<string, string> = {
  nouveau_lot_reactif: 'Nouveau lot réactif',
  valeur_aberrante: 'Valeur aberrante',
  linearite_courbe: 'Linéarité de la courbe',
};

/** Motifs qui DISENT explicitement qu'une calibration a été faite — un nouveau
 *  lot réactif ou une vérification de linéarité en sont, par construction,
 *  l'objet même. « Valeur aberrante » n'implique aucune recalibration, juste
 *  une reprise de mesure : ne pas ouvrir la fiche à sa place la viderait de
 *  son sens (05/09/2026, demande d'Inès — « quand le technicien mentionne
 *  qu'il a fait une cal »). */
const MOTIFS_IMPLIQUANT_CAL = new Set(['nouveau_lot_reactif', 'linearite_courbe']);

const MOTIFS: Record<string, string> = {
  controle_banni: 'Graphie abandonnée',
  controle_inconnu: 'ID de contrôle non reconnu (ni un alias déclaré, ni sans doute un point CQI)',
  test_inconnu: 'Libellé de test absent des alias connus pour cet analyte',
  valeur_illisible: 'Résultat non numérique',
  carte_absente: 'Aucune carte CQI pour cet analyte sur cet équipement',
  periode_fermee: 'Aucun lot de contrôle ouvert pour cette carte',
  lot_differe: 'Cette carte n’est pas encore sur le lot choisi',
  // R3 (07/10/2026) — le lot de contrôle n'est pas valable à la date du rapport
  // (périmé, pas encore ouvert, refermé) : la ligne est écartée, le détail nomme
  // le lot et la date.
  lot_inutilisable: 'Lot de contrôle non valable à la date du rapport',
};

/** Même convention de couleur que le reste du module CQI (`Cqi.tsx`) :
 *  |z| ≥ 3 rouge, ≥ 2 jaune, sinon vert — sauf si une règle DÉCLENCHÉE de la
 *  tentative désigne explicitement ce niveau (ou les deux, `niveau: null`) :
 *  elle prime, un rejet inter-niveaux (R-4s) ne se voit pas sur le seul z
 *  individuel. */
function couleurCase(l: LigneAffichee): { fond: string; texte: string } {
  if (l.acceptable === null) return { fond: 'var(--gray-50)', texte: 'var(--text-muted)' };
  const regle = l.reglesPrevu.find((r) => r.niveau === null || r.niveau === l.niveau);
  const grave = regle?.gravite === 'rejet' || (!regle && l.acceptable === false && Math.abs(l.z ?? 0) >= 3);
  const alerte = regle?.gravite === 'alerte' || (!regle && Math.abs(l.z ?? 0) >= 2);
  if (grave) return { fond: 'var(--red-50)', texte: 'var(--red-700)' };
  if (alerte) return { fond: 'var(--amber-50)', texte: 'var(--amber-700)' };
  return { fond: 'var(--green-50)', texte: 'var(--green-700)' };
}

const cle = (page: number, ligneIndex: number) => `${page}:${ligneIndex}`;

function SelecteurNiveau({ page, ligneIndex, valeur, options, onChoisir }: {
  page: number; ligneIndex: number; valeur?: number;
  options: { niveau: number; label: string }[];
  onChoisir: (page: number, ligneIndex: number, patch: Correction) => void;
}) {
  return (
    <select value={valeur ?? ''} style={{ ...inputStyle, fontSize: 11.5, padding: '2px 4px', width: 120 }}
      onChange={(e) => onChoisir(page, ligneIndex, { niveau: e.target.value ? Number(e.target.value) : undefined })}>
      <option value="">— niveau —</option>
      {options.map((n) => <option key={n.niveau} value={n.niveau}>{n.label}</option>)}
    </select>
  );
}
function SelecteurAnalyte({ page, ligneIndex, valeur, options, onChoisir }: {
  page: number; ligneIndex: number; valeur?: string;
  options: { code: string; nom: string }[];
  onChoisir: (page: number, ligneIndex: number, patch: Correction) => void;
}) {
  return (
    <select value={valeur ?? ''} style={{ ...inputStyle, fontSize: 11.5, padding: '2px 4px', width: 180 }}
      onChange={(e) => onChoisir(page, ligneIndex, { analyteCode: e.target.value || undefined })}>
      <option value="">— test (détection automatique) —</option>
      {options.map((a) => <option key={a.code} value={a.code}>{a.nom}</option>)}
    </select>
  );
}

/** Carte + graphe simplifié ouverts DEPUIS la liste d'aperçu, sans quitter
 *  l'import — la seule question qu'on se pose en corrigeant une ligne
 *  (« est-ce cohérent avec l'historique ? ») ne devrait pas obliger à
 *  abandonner l'import pour aller la vérifier ailleurs. Lecture seule : ce
 *  point n'est pas encore enregistré, il n'apparaît donc PAS sur ce graphe. */
function CarteApercuModale({ carteId, analyteNom, onClose }: { carteId: string; analyteNom: string; onClose: () => void }) {
  const [detail, setDetail] = useState<{ carte: Ctx; blocs: Bloc[] } | null>(null);
  const [erreur, setErreur] = useState('');
  React.useEffect(() => {
    let vivant = true;
    fetch(`/api/ged/ged/cqi-cartes/${carteId}?mode=lot`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => { if (vivant) setDetail(d); })
      .catch(() => { if (vivant) setErreur('Carte introuvable ou lot non ouvert.'); });
    return () => { vivant = false; };
  }, [carteId]);

  return (
    <Modale titre={`Carte CQI — ${analyteNom}`} onClose={onClose} largeur={960}>
      {erreur && <p style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</p>}
      {!erreur && !detail && <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Chargement…</p>}
      {detail && (detail.blocs.length === 0 || !detail.blocs.some((b) => b.points.length) ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun passage encore enregistré sur le lot en cours.</p>
      ) : (
        <LeveyJennings
          blocs={detail.blocs} nbNiveaux={detail.carte.nbNiveaux as number}
          onPoint={() => {}} selection={null}
          unite={(detail.carte.unite as string) || null} libelles={(detail.carte.libellesNiveaux as string[]) || []}
        />
      ))}
    </Modale>
  );
}

/** Les deux temps de l'import, dits en haut de l'écran. Une étape qui ne se
 *  nomme pas se lit comme un écran de plus, et l'on cherche le bouton
 *  « enregistrer » à la première. */
function EtapesImport({ actuelle }: { actuelle: 'detection' | 'revue' }) {
  const etapes = [
    ['detection', '1 · Ce que le fichier contient'],
    ['revue', '2 · Revue, paramètre par paramètre'],
  ] as const;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {etapes.map(([cle, libelle]) => {
        const ici = cle === actuelle;
        return (
          <span key={cle} style={{
            fontSize: 12, fontWeight: ici ? 700 : 500, padding: '4px 11px',
            borderRadius: 'var(--radius-pill)',
            background: ici ? 'var(--color-primary)' : 'var(--surface-sunken)',
            color: ici ? 'var(--surface-card)' : 'var(--text-muted)',
            border: `1px solid ${ici ? 'var(--color-primary)' : 'var(--border-default)'}`,
          }}>{libelle}</span>
        );
      })}
    </div>
  );
}

/**
 * LE RAPPORT LUI-MÊME, À CÔTÉ DE CE QU'ON EN A TIRÉ.
 *
 * ── Le retour (Manel, 11/09/2026) ──────────────────────────────────────────
 *
 * « Afficher le pdf importé au moment de l'aperçu de l'import. »
 *
 * Le fichier était lu en base64, envoyé au serveur, et jamais montré : on
 * validait un tableau de chiffres sans pouvoir le confronter à sa source. Or
 * c'est exactement ce que l'étape de détection demande — « ces vingt-deux
 * points sont-ils bien ceux du rapport ? ».
 *
 * ⚠️ REPLIÉ PAR DÉFAUT. Un PDF ouvert pousse le tableau hors de l'écran, et
 * l'on retomberait sur le défaut du lot 2 : la chose qu'on cherche existe, mais
 * plus bas que le regard. On l'ouvre quand on doute.
 *
 * ⚠️ `data:` ET NON UNE URL DE SERVEUR : le fichier n'est pas déposé — il n'est
 * envoyé que pour être lu, et rien n'en garde de copie. L'afficher depuis une
 * adresse supposerait de le stocker, c'est-à-dire de changer ce que fait cet
 * écran pour le seul confort de l'afficher.
 */
function ApercuFichier({ b64, nom }: { b64: string; nom: string }) {
  const [ouvert, setOuvert] = useState(false);
  const estPdf = /\.pdf$/i.test(nom);
  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOuvert(!ouvert)} className="ged-btn"
        style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, border: 'none', background: 'var(--surface-sunken)', padding: '9px 12px', fontSize: 12.5, cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-sans)' }}>
        <I n={ouvert ? 'chevron-down' : 'chevron-right'} s={14} />
        <strong>{ouvert ? 'Masquer' : 'Afficher'} le rapport importé</strong>
        <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{nom}</span>
      </button>
      {ouvert && (estPdf ? (
        <object data={`data:application/pdf;base64,${b64}`} type="application/pdf"
          style={{ width: '100%', height: 460, display: 'block', border: 'none' }}>
          {/* Tous les navigateurs n'affichent pas un PDF en ligne — et un cadre
              vide se lirait « le fichier est illisible », ce qui est faux. */}
          <p style={{ fontSize: 12.5, padding: 12, margin: 0 }}>
            Votre navigateur n’affiche pas les PDF dans la page.{' '}
            <a href={`data:application/pdf;base64,${b64}`} download={nom}>Ouvrir le fichier</a>
          </p>
        </object>
      ) : (
        <p style={{ fontSize: 12.5, padding: 12, margin: 0, color: 'var(--text-muted)' }}>
          Ce format ne s’affiche pas dans la page.{' '}
          <a href={`data:application/octet-stream;base64,${b64}`} download={nom}>Ouvrir le fichier</a>
        </p>
      ))}
    </div>
  );
}

/**
 * LA REVUE D'UN PARAMÈTRE — la carte, AVEC le point candidat dessus.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Pourquoi cette étape existe (RQ, 12/09/2026) ───────────────────────────
 *
 * « En routine, la détection de non-linéarité se fera directement sur la
 *  machine et la repasse automatique, mais 2-1s ou 2-2s ou 3-1s ne sera
 *  détectée qu'au moment du chargement des données sur l'outil. Donc à ce
 *  moment je veux voir le nouveau point (à ajouter en couleur plus claire)
 *  jusqu'à sa validation. Puis ça passe au paramètre d'après. »
 *
 * ⚠️ C'EST UNE RAISON TECHNIQUE, PAS UNE PRÉFÉRENCE D'ERGONOMIE, et c'est ce
 * qui la rend non négociable. Une règle de Westgard multipoint — 2-1s, 2-2s,
 * 3-1s, 4-1s, 10-x — ne se lit PAS sur un point isolé : elle porte sur une
 * SUITE. Le tableau de l'aperçu, qui montre une ligne par niveau, ne pouvait
 * donc pas la faire voir. Seule la courbe le peut, et seulement si le point
 * candidat y figure. L'automate, lui, ne voit que ce qu'il vient de mesurer :
 * il attrape la non-linéarité et repasse tout seul, mais il ne connaît pas
 * l'historique de la carte. C'est précisément ce que l'outil ajoute — et ce
 * qu'on ne pouvait pas regarder avant de valider.
 *
 * ── Ce qui est montré, et ce qui ne l'est pas ──────────────────────────────
 *
 * La carte du LOT EN COURS, telle qu'elle est aujourd'hui, plus le point du
 * rapport en cours de revue — creux et cerclé de tirets (`provisoires`). Le
 * verdict affiché est celui CALCULÉ PAR LE SERVEUR à l'aperçu : on ne recalcule
 * rien ici, sans quoi deux moteurs de règles cohabiteraient.
 *
 * ⚠️ LE POINT EST DESSINÉ MÊME S'IL SERA REFUSÉ. C'est en le voyant qu'on
 * décide — le masquer dès le refus ferait disparaître ce qu'on est en train
 * de juger.
 */
function RevueParametre({ ligne, date, accepte, onAccepte, motif, onMotif,
  motifRepasse, onMotifRepasse, exigeMotifRepasse, lotReactif, onLotReactif, note, onNote, onCalibration }: {
  ligne: LigneReconnue;
  date: string;
  /** Par NIVEAU : le point est-il retenu ? Le refus exige un motif. */
  accepte: Record<number, boolean>;
  onAccepte: (niveau: number, v: boolean) => void;
  motif: Record<number, string>;
  onMotif: (niveau: number, v: string) => void;
  motifRepasse: string;
  onMotifRepasse: (v: string) => void;
  exigeMotifRepasse: boolean;
  lotReactif: string;
  onLotReactif: (v: string) => void;
  note: string;
  onNote: (v: string) => void;
  onCalibration: () => void;
}) {
  const [detail, setDetail] = useState<{ carte: Ctx; blocs: Bloc[] } | null>(null);
  const [erreur, setErreur] = useState('');
  /* ⚠️ AUCUNE REMISE À ZÉRO ICI. L'appelant monte ce composant avec une `key`
     par paramètre : changer de paramètre le REMONTE, et l'état repart neuf de
     lui-même. Le remettre à zéro en tête d'effet serait un `setState` synchrone
     dans un effet — refusé par le compilateur React, et à raison : il relance
     un rendu pour arriver à l'état qu'un remontage donne gratuitement. */
  React.useEffect(() => {
    let vivant = true;
    fetch(`/api/ged/ged/cqi-cartes/${ligne.carteId}?mode=lot`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => { if (vivant) setDetail(d); })
      .catch(() => { if (vivant) setErreur('La carte n’a pas pu être lue — le graphe n’est pas affichable.'); });
    return () => { vivant = false; };
  }, [ligne.carteId]);

  /* Le point CANDIDAT, ajouté à la fin du lot en cours. Son `passageId` est une
     constante reconnaissable : c'est elle qui le marque provisoire auprès du
     graphe, et elle ne peut entrer en collision avec aucun documentId. */
  const PROVISOIRE = '__candidat__';
  const blocs: Bloc[] = useMemo(() => {
    if (!detail) return [];
    const point = {
      passageId: PROVISOIRE, date, numeroSerie: null, operateur: null,
      lotReactif: lotReactif || null,
      valeurs: ligne.niveaux.map((n) => ({
        niveau: n.niveau, valeur: n.valeur, z: n.z,
        rejete: accepte[n.niveau] === false, rejeteMotif: motif[n.niveau] || null,
      })),
      commentaire: null, validation: 'attente', motifDerogation: null, importe: true,
      regles: ligne.reglesPrevu, statut: ligne.statutPrevu,
      repasse: ligne.numeroTentative > 1,
      couleur: ligne.statutPrevu === 'rejet' ? 'rouge' : ligne.statutPrevu === 'alerte' ? 'jaune' : 'vert',
      enAttente: false, suite: null, exige: null,
    } as unknown as Point;
    /* Sur le DERNIER bloc : c'est le lot en cours, celui contre lequel le point
       a été évalué (`resoudreCarteEtPeriode` l'a confirmé avant l'aperçu). */
    return detail.blocs.map((b, i) => (i === detail.blocs.length - 1
      ? { ...b, points: [...b.points, point] } : b));
  }, [detail, ligne, date, accepte, motif, lotReactif]);

  const ton = ligne.statutPrevu === 'rejet' ? 'var(--red-600)'
    : ligne.statutPrevu === 'alerte' ? 'var(--amber-600)' : 'var(--green-600)';

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 15, color: 'var(--text-strong)' }}>{ligne.analyteNom}</strong>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>{ligne.analyteCode}</span>
        {ligne.numeroTentative > 1 && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--amber-600)' }}>tentative n°{ligne.numeroTentative}</span>
        )}
        <span style={{ flex: 1 }} />
        {/* Le verdict vient du SERVEUR (même moteur que l'écriture) : on
            l'affiche, on ne le recalcule pas. */}
        <span style={{ fontSize: 12.5, fontWeight: 700, color: ton }}>
          {ligne.statutPrevu === 'rejet' ? 'Rejet' : ligne.statutPrevu === 'alerte' ? 'Avertissement' : 'Conforme'}
          {ligne.reglesPrevu.length ? ` · ${ligne.reglesPrevu.map((r) => r.code).join(', ')}` : ''}
        </span>
      </div>

      {erreur && <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0 }}>{erreur}</p>}
      {!erreur && !detail && <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Lecture de la carte…</p>}
      {detail && (
        <>
          <LeveyJennings
            blocs={blocs} nbNiveaux={detail.carte.nbNiveaux as number}
            onPoint={() => {}} selection={null}
            unite={(detail.carte.unite as string) || null}
            libelles={(detail.carte.libellesNiveaux as string[]) || []}
            provisoires={[PROVISOIRE]} />
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
            Le point du rapport est tracé <strong>creux et cerclé de tirets</strong> : il n’est pas encore
            enregistré. C’est sur cette courbe que se lisent les règles qui portent sur une SUITE
            (2-1s, 2-2s, 3-1s…), et que l’automate ne peut pas voir.
          </p>
        </>
      )}

      {/* LES POINTS, NIVEAU PAR NIVEAU — accepté par défaut selon le verdict,
          l'opérateur garde la main. Un refus exige un motif (même règle que le
          rejet a posteriori). */}
      <div style={{ display: 'grid', gap: 8 }}>
        {ligne.niveaux.map((n) => {
          const ok = accepte[n.niveau] !== false;
          return (
            <div key={n.niveau} style={{ display: 'grid', gap: 6, padding: '9px 11px', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 12.5 }}>Niveau {n.niveau}</strong>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{n.valeur}{n.unite ? ` ${n.unite}` : ''}</span>
                {typeof n.z === 'number' && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: Math.abs(n.z) >= 3 ? 'var(--red-600)' : Math.abs(n.z) >= 2 ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                    {n.z >= 0 ? '+' : ''}{n.z.toFixed(2)}σ
                  </span>
                )}
                {!!n.erreursAutomate && (
                  <span style={{ fontSize: 11.5, color: 'var(--amber-600)' }}>automate : {n.erreursAutomate}</span>
                )}
                <span style={{ flex: 1 }} />
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={ok} onChange={(e) => onAccepte(n.niveau, e.target.checked)} />
                  {ok ? 'Retenu sur la carte' : 'Écarté'}
                </label>
              </div>
              {!ok && (
                <input value={motif[n.niveau] || ''} onChange={(e) => onMotif(n.niveau, e.target.value)}
                  placeholder="Pourquoi ce point est-il écarté ? (5 caractères minimum)"
                  style={{ ...inputStyle, fontSize: 12.5 }} />
              )}
            </div>
          );
        })}
      </div>

      {/* CE QU'ON RACONTE — « on raconte s'il y a des soucis, si une nouvelle
          cal a été faite ou bien un nouveau lot de réactifs » (RQ, 12/09). */}
      <fieldset style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '11px 13px', margin: 0, display: 'grid', gap: 10 }}>
        <legend style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', padding: '0 6px' }}>
          Ce qui s’est passé sur ce paramètre
        </legend>
        {exigeMotifRepasse && (
          <label style={{ fontSize: 12.5 }}>Pourquoi ce paramètre a-t-il été repassé ? *
            <select value={motifRepasse} onChange={(e) => onMotifRepasse(e.target.value)} style={{ ...inputStyle, fontSize: 12.5 }}>
              <option value="">— choisir —</option>
              {Object.entries(MOTIFS_REPASSE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
        )}
        <label style={{ fontSize: 12.5 }}>Lot de réactif <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— si différent du lot commun à la série</span>
          <input value={lotReactif} onChange={(e) => onLotReactif(e.target.value)}
            placeholder="laisser vide si c’est le même" style={{ ...inputStyle, fontSize: 12.5 }} />
        </label>
        <label style={{ fontSize: 12.5 }}>Observation <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— facultative, enregistrée avec le passage</span>
          <textarea value={note} onChange={(e) => onNote(e.target.value)}
            placeholder="ce qu’il faut savoir pour relire ce point dans six mois"
            style={{ ...inputStyle, fontSize: 12.5, minHeight: 52 }} />
        </label>
        {/* ⚠️ La fiche de calibration s'ouvre sur DEMANDE ici, et
            AUTOMATIQUEMENT quand le motif de repasse dit qu'une calibration a
            eu lieu (`MOTIFS_IMPLIQUANT_CAL`) — les deux chemins existent parce
            qu'une calibration peut avoir été faite sans repasse. */}
        <Bouton taille="sm" type="button" onClick={onCalibration} style={{ width: 'fit-content' }}>
          <I n="plus" s={13} />Une calibration a été faite
        </Bouton>
      </fieldset>
    </div>
  );
}

/**
 * LA REVUE, PARAMÈTRE PAR PARAMÈTRE — un seul à la fois, jusqu'au bout.
 *
 * ── Ce que la RQ a demandé (12/09/2026) ────────────────────────────────────
 *
 * « L'aperçu s'affiche paramètre par paramètre, à la suite de la liste prise en
 *  compte, et à ce moment on regarde le graphe et on valide sa prise en compte
 *  ou pas. […] Puis ça passe au paramètre d'après, et ainsi de suite jusqu'à la
 *  fin. »
 *
 * ⚠️ UN SEUL À LA FOIS, ET C'EST LE POINT. Le tableau précédent montrait tout
 * ensemble : on pouvait le parcourir des yeux et confirmer sans avoir rien
 * regardé. Ici, chaque paramètre demande un geste — et le geste demandé est
 * celui qu'on ne pouvait pas faire : lire la COURBE. Une règle multipoint
 * (2-1s, 2-2s, 3-1s) ne se voit nulle part ailleurs.
 *
 * ⚠️ ON PEUT REVENIR EN ARRIÈRE, et on peut sauter au dernier. Forcer un
 * parcours strictement linéaire sur vingt-cinq paramètres transformerait une
 * revue en corvée, et une corvée se traverse sans regarder — c'est-à-dire
 * qu'on retrouverait le défaut qu'on vient de corriger, avec un clic de plus.
 *
 * ⚠️ CE QUI RESTE À FAIRE EST DIT AVANT LE BOUTON, pas après le refus : la
 * confirmation reste barrée tant qu'un motif manque, mais l'écran nomme lequel
 * et sur quel paramètre.
 */
function RevueSequentielle({
  lignes, iCourant, onI, date, nbGardeesParAnalyte,
  pointsRejetes, setPointsRejetes, pointsMotifs, setPointsMotifs,
  motifsRepasse, setMotifsRepasse, lotsParLigne, setLotsParLigne,
  notesParLigne, setNotesParLigne, onCalibration, attentes, occupe, enCours,
  onRetour, onConfirmer,
}: {
  lignes: LigneReconnue[];
  iCourant: number; onI: (i: number) => void;
  date: string;
  nbGardeesParAnalyte: Map<string, number>;
  pointsRejetes: Record<string, boolean>; setPointsRejetes: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  pointsMotifs: Record<string, string>; setPointsMotifs: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  motifsRepasse: Record<string, string>; setMotifsRepasse: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  lotsParLigne: Record<string, string>; setLotsParLigne: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  notesParLigne: Record<string, string>; setNotesParLigne: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onCalibration: (l: LigneReconnue) => void;
  attentes: { ok: boolean; motifsManquants: number; rejetsSansMotif: number };
  occupe: boolean; enCours: boolean;
  onRetour: () => void; onConfirmer: () => void;
}) {
  /* ⚠️ JAMAIS UN ÉCRAN VIDE. `iCourant` peut sortir de la liste si l'on décoche
     des tentatives après être entré en revue ; un `return null` laisserait une
     fenêtre blanche sans aucune sortie — un vide qui se lit « c'est cassé ». On
     borne, et si la liste est vraiment vide on le DIT, avec le chemin du
     retour. */
  const l = lignes[Math.min(iCourant, lignes.length - 1)];
  if (!l) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <EtapesImport actuelle="revue" />
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
          Plus aucun paramètre à revoir : tout a été décoché à l’étape précédente.
        </p>
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Bouton onClick={onRetour}>← Revenir à la détection</Bouton>
        </div>
      </div>
    );
  }
  const cle = `${l.analyteCode}#${l.numeroTentative}`;
  const rang = Math.min(iCourant, lignes.length - 1);
  const dernier = rang >= lignes.length - 1;

  /* Ce qui manque SUR CE PARAMÈTRE — distinct du décompte global : c'est lui
     qui doit barrer le passage au suivant, et le nommer. */
  const motifRepasseDu = exigeRepasse(l, nbGardeesParAnalyte) && !motifsRepasse[cle];
  const refusSansMotif = l.niveaux.some((n) => pointsRejetes[`${cle}#${n.niveau}`]
    && (pointsMotifs[`${cle}#${n.niveau}`] || '').trim().length < 5);
  const bloque = motifRepasseDu || refusSansMotif;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <EtapesImport actuelle="revue" />

      {/* OÙ L'ON EN EST — et de quoi sauter. Les pastilles disent aussi le
          verdict calculé : on voit d'avance où sont les ennuis. */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
        {lignes.map((x, i) => {
          const ici = i === rang;
          const ton = x.statutPrevu === 'rejet' ? 'var(--red-500)'
            : x.statutPrevu === 'alerte' ? 'var(--amber-500)' : 'var(--green-500)';
          return (
            <button key={`${x.analyteCode}#${x.numeroTentative}`} type="button" className="ged-btn"
              onClick={() => onI(i)} title={`${x.analyteNom} — ${x.statutPrevu}`}
              style={{
                cursor: 'pointer', fontSize: 11.5, lineHeight: 1.2, padding: '3px 8px',
                borderRadius: 'var(--radius-pill)', fontWeight: ici ? 700 : 400,
                border: `1px solid ${ici ? 'var(--color-primary)' : 'var(--border-default)'}`,
                background: ici ? 'var(--color-primary)' : 'transparent',
                color: ici ? 'var(--surface-card)' : 'var(--text-muted)',
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: ton, display: 'inline-block' }} />
              {x.analyteCode}
            </button>
          );
        })}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          {rang + 1} / {lignes.length}
        </span>
      </div>

      <RevueParametre
        key={cle}
        ligne={l} date={date}
        accepte={Object.fromEntries(l.niveaux.map((n) => [n.niveau, !pointsRejetes[`${cle}#${n.niveau}`]]))}
        onAccepte={(niveau, v) => setPointsRejetes((m) => ({ ...m, [`${cle}#${niveau}`]: !v }))}
        motif={Object.fromEntries(l.niveaux.map((n) => [n.niveau, pointsMotifs[`${cle}#${n.niveau}`] || '']))}
        onMotif={(niveau, v) => setPointsMotifs((m) => ({ ...m, [`${cle}#${niveau}`]: v }))}
        motifRepasse={motifsRepasse[cle] || ''}
        onMotifRepasse={(v) => setMotifsRepasse((m) => ({ ...m, [cle]: v }))}
        exigeMotifRepasse={exigeRepasse(l, nbGardeesParAnalyte)}
        lotReactif={lotsParLigne[cle] || ''}
        onLotReactif={(v) => setLotsParLigne((m) => ({ ...m, [cle]: v }))}
        note={notesParLigne[cle] || ''}
        onNote={(v) => setNotesParLigne((m) => ({ ...m, [cle]: v }))}
        onCalibration={() => onCalibration(l)} />

      {bloque && (
        <p style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600, margin: 0 }}>
          {motifRepasseDu ? 'Ce paramètre a été repassé : dites pourquoi avant de continuer.' : ''}
          {motifRepasseDu && refusSansMotif ? ' ' : ''}
          {refusSansMotif ? 'Un point écarté demande un motif d’au moins 5 caractères.' : ''}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'end', flexWrap: 'wrap' }}>
        <Bouton onClick={onRetour}>← Revenir à la détection</Bouton>
        <Bouton disabled={rang === 0} onClick={() => onI(rang - 1)}>Précédent</Bouton>
        {!dernier ? (
          <Bouton variante="primaire" disabled={bloque} onClick={() => onI(rang + 1)}>Paramètre suivant →</Bouton>
        ) : (
          <Bouton variante="primaire" disabled={!attentes.ok || occupe} onClick={onConfirmer}>
            {enCours ? 'Enregistrement…' : `Enregistrer les ${lignes.length} paramètre(s)`}
          </Bouton>
        )}
      </div>

      {/* Au DERNIER paramètre, ce qui reste dû ailleurs dans la revue : sans
          cette phrase, le bouton barré au bout du parcours ne dirait pas où
          retourner. */}
      {dernier && !attentes.ok && !bloque && (
        <p style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600, margin: 0, textAlign: 'right' }}>
          {attentes.motifsManquants > 0 && <>{attentes.motifsManquants} repasse(s) sans motif. </>}
          {attentes.rejetsSansMotif > 0 && <>{attentes.rejetsSansMotif} point(s) écarté(s) sans motif. </>}
          Reprenez les pastilles ci-dessus.
        </p>
      )}
    </div>
  );
}

/** Un analyte repassé dans le même import doit dire POURQUOI : sans motif, la
 *  seconde tentative se lit comme un doublon de saisie et non comme une reprise
 *  décidée. Même règle qu'au serveur, qui la revérifie. */
function exigeRepasse(l: LigneReconnue, nbGardeesParAnalyte: Map<string, number>): boolean {
  return (nbGardeesParAnalyte.get(l.analyteCode) || 0) > 1;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Ctx = any;

function lireFichierB64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const d = String(r.result || '');
      resolve(d.slice(d.indexOf(',') + 1));
    };
    r.onerror = () => reject(new Error('Fichier illisible.'));
    r.readAsDataURL(f);
  });
}

export function CqiImportPdf({ automates, equipementCodeDefaut, onImporte }: {
  automates: { code: string; nom: string }[];
  equipementCodeDefaut?: string;
  onImporte: () => void;
}) {
  const { showToast, session, settings } = useGed();
  // Non dérogeable sauf pour un « responsable » (06/09/2026, demande
  // d'Inès) — réutilise le droit de dérogation CQI déjà existant (le
  // raccourci `estBiologiste`) plutôt que d'en inventer un : manager/
  // superadmin l'ont par défaut, technician non.
  const peutDeroger = estBiologiste(session.profile, settings?.perms);
  const [ouvert, setOuvert] = useState(false);
  // AUCUN repli sur le premier automate de la liste : un équipement CHOISI,
  // jamais deviné — voir la garde sur le champ fichier plus bas.
  const [equipementCode, setEquipementCode] = useState(equipementCodeDefaut || '');
  // Les lots OUVERTS sur l'équipement choisi — chargés dès qu'il change,
  // AVANT tout fichier : c'est ce choix qui dit contre quelle carte chaque
  // point sera évalué, pas une confirmation qu'on ferait après coup.
  const [lotsDisponibles, setLotsDisponibles] = useState<{ lot: string; cartes: number }[] | null>(null);
  const [lotControle, setLotControle] = useState('');
  const [fichierB64, setFichierB64] = useState('');
  const [nomFichier, setNomFichier] = useState('');
  const [apercu, setApercu] = useState<ReponseApercu | null>(null);
  const [date, setDate] = useState('');
  const [operateur, setOperateur] = useState('');
  /* ⚠️ LE LOT DE RÉACTIF, COMMUN À LA SÉRIE (12/09/2026). Il n'existait nulle
     part dans l'import : `cqi-import/valider` ne le transmettait pas, et la
     colonne était donc vide sur 7 654 passages sur 8 101. Sans lui, le trait
     vertical demandé le 11/09 (« afficher le début d'un nouveau lot de
     réactifs par un trait sur la courbe ») ne se serait presque jamais dessiné
     — et l'on en aurait conclu que le laboratoire ne change jamais de lot.
     ⚠️ FACULTATIF : un rapport d'automate ne porte pas cette information. La
     rendre obligatoire ferait perdre des imports entiers pour une donnée que
     l'opérateur n'a pas toujours sous la main.
     ⚠️ EN TEXTE LIBRE, comme à la saisie manuelle — et c'est provisoire : la
     liste déroulante (retour 021) attend le registre des lots, encore vide.
     Arbitrage de la RQ du 12/09. */
  const [lotReactif, setLotReactif] = useState('');
  const [gardees, setGardees] = useState<Record<string, boolean>>({});
  const [corrections, setCorrections] = useState<Record<string, Correction>>({});
  const [vue, setVue] = useState<'parametre' | 'niveau'>('parametre');
  // Motif de repasse — une entrée par TENTATIVE (`attemptKey`), exigée dès
  // qu'un analyte compte plus d'une tentative gardée dans cet import.
  const [motifsRepasse, setMotifsRepasse] = useState<Record<string, string>>({});
  // Accepté/refusé — proposé depuis le verdict calculé, mais l'opérateur garde
  // la main : clé `${attemptKey}#${niveau}`. Un point refusé exige un motif
  // (même règle que le rejet a posteriori, `cqi-points.ts`, ≥ 5 caractères).
  const [pointsRejetes, setPointsRejetes] = useState<Record<string, boolean>>({});
  const [pointsMotifs, setPointsMotifs] = useState<Record<string, string>>({});
  const [carteOuverte, setCarteOuverte] = useState<{ carteId: string; analyteNom: string } | null>(null);
  /* ── LE PARCOURS EN DEUX TEMPS (RQ, 12/09/2026) ─────────────────────────────
   *
   * « Donc une étape pour valider la détection des points par l'importateur (y
   *  afficher le nombre de points détectés) et ensuite paramètre par paramètre
   *  on valide. »
   *
   * `detection` répond à « le fichier a-t-il été LU correctement » — on compare
   * au PDF, affiché à côté, et on compte. `revue` répond à « ce point a-t-il sa
   * place sur la carte » — une question tout autre, qui demande la COURBE et
   * non un tableau. Les mélanger, c'était demander les deux à la fois sur un
   * écran qui ne pouvait en montrer qu'une.
   *
   * ⚠️ On ne quitte pas `detection` sans avoir décoché ce qui ne doit pas
   * entrer : la revue porte sur ce qui a été GARDÉ. */
  const [etape, setEtape] = useState<'detection' | 'revue'>('detection');
  /** Rang du paramètre en cours de revue, dans l'ordre des lignes gardées. */
  const [iRevue, setIRevue] = useState(0);
  /** Ce qu'on raconte, par tentative : lot de réactif propre, observation. */
  const [lotsParLigne, setLotsParLigne] = useState<Record<string, string>>({});
  const [notesParLigne, setNotesParLigne] = useState<Record<string, string>>({});
  /** Ouverte dès que le motif choisi DIT qu'une calibration a été faite — pas
   *  après confirmation de l'import : c'est le fait de le mentionner qui
   *  déclenche la fiche, pas l'écriture qui suit. */
  const [calibrationOuverte, setCalibrationOuverte] = useState<
    { equipementCode: string; analyteLibelle: string; analyteCode: string; serie: string; regle: string } | null
  >(null);
  /** Coché par un « responsable » qui a DÉJÀ le droit sur son propre poste —
   *  jamais pré-coché. Qui ne l'a pas voit `ChampsResponsable` à la place
   *  (06/09/2026) : un responsable peut alors autoriser depuis LE POSTE DU
   *  TECHNICIEN, identifiant + mot de passe, sans ouvrir de session. */
  const [forcerAlerteMachine, setForcerAlerteMachine] = useState(false);
  const authResponsableMachine = useAutorisationResponsable();
  /** Un aperçu affiché ne correspond plus forcément à ce qui serait écrit dès
   *  qu'une correction est en attente — le bouton de confirmation reste
   *  bloqué jusqu'au recalcul. */
  const [dirty, setDirty] = useState(false);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  // Les lots OUVERTS sur l'équipement — rechargés à chaque changement
  // d'équipement, jamais conservés d'un automate à l'autre. Le lot choisi
  // est remis à zéro en même temps : un lot valable sur EQ01 ne veut rien
  // dire sur EQ02.
  const [equipementPrecedent, setEquipementPrecedent] = useState(equipementCode);
  if (equipementPrecedent !== equipementCode) {
    setEquipementPrecedent(equipementCode);
    setLotControle('');
    setLotsDisponibles(null);
  }
  React.useEffect(() => {
    let vivant = true;
    if (!equipementCode) return undefined;
    fetch(`/api/ged/ged/cqi-import/lots?equipementCode=${encodeURIComponent(equipementCode)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => { if (vivant) setLotsDisponibles(Array.isArray(d.lots) ? d.lots : []); })
      .catch(() => { if (vivant) setLotsDisponibles([]); });
    return () => { vivant = false; };
  }, [equipementCode]);

  const fermer = () => {
    setOuvert(false); setFichierB64(''); setNomFichier(''); setApercu(null); setErreur('');
    setCorrections({}); setDirty(false); setMotifsRepasse({});
    setPointsRejetes({}); setPointsMotifs({}); setForcerAlerteMachine(false);
    authResponsableMachine.reinitialiser();
  };

  const choisirFichier = async (f: File | null) => {
    if (!f) return;
    setNomFichier(f.name);
    setApercu(null); setCorrections({}); setDirty(false); setMotifsRepasse({});
    setPointsRejetes({}); setPointsMotifs({}); setForcerAlerteMachine(false);
    authResponsableMachine.reinitialiser();
    try { setFichierB64(await lireFichierB64(f)); } catch { setErreur('Ce fichier ne peut pas être lu.'); }
  };

  const majCorrection = (page: number, ligneIndex: number, patch: Correction) => {
    setCorrections((c) => ({ ...c, [cle(page, ligneIndex)]: { ...c[cle(page, ligneIndex)], ...patch } }));
    setDirty(true);
  };

  const appliquerReponse = (d: ReponseApercu) => {
    setApercu(d);
    setDirty(false);
    // Les tentatives déjà cochées le restent ; une tentative qui vient
    // d'apparaître (une correction l'a fait passer en reconnue) démarre cochée.
    setGardees((g) => {
      const next = { ...g };
      for (const l of d.reconnues) {
        const k = `${l.analyteCode}#${l.numeroTentative}`;
        /* ⚠️ UN DOUBLON DÉTECTÉ DÉMARRE DÉCOCHÉ (12/09/2026) — « un point ne
           doit pas être importé deux fois dans la courbe » (Manel, 11/09).
           Décoché et non refusé : le geste par défaut devient « ne pas
           réécrire », et cocher redevient une décision qu'on prend en voyant
           ce qui a été trouvé. */
        if (!(k in next)) next[k] = !l.doublon;
      }
      return next;
    });
    // Accepté/refusé PROPOSÉ depuis le verdict — seulement pour une clé
    // encore jamais vue : un choix déjà fait par l'opérateur (avant un
    // recalcul d'aperçu) n'est jamais repris en silence. Un point que
    // l'AUTOMATE a lui-même flagué (« Linéarité », …) est proposé refusé au
    // même titre qu'un verdict Westgard rejeté — ce que la machine a noté
    // n'a pas à être recopié à la main avant d'être écarté du calcul de
    // moyenne (06/09/2026, demande d'Inès).
    /* LE MOTIF DE REPASSE QUE L'AUTOMATE A DÉJÀ DIT (09/09/2026, RQ : « quand
       le motif de la repasse est déjà connu, le saisir automatiquement »).
       Posé seulement sur une tentative encore jamais vue, et seulement si
       l'automate l'a imprimé sans ambiguïté : un motif déjà choisi par
       l'opérateur n'est jamais réécrit, et le champ reste modifiable. */
    setMotifsRepasse((m) => {
      const next = { ...m };
      for (const l of d.reconnues) {
        const k = `${l.analyteCode}#${l.numeroTentative}`;
        if (!(k in next) && l.motifRepasseDeduit) next[k] = l.motifRepasseDeduit;
      }
      return next;
    });
    setPointsRejetes((p) => {
      const next = { ...p };
      for (const l of d.reconnues) {
        const k0 = `${l.analyteCode}#${l.numeroTentative}`;
        for (const n of l.niveaux) {
          const k = `${k0}#${n.niveau}`;
          if (!(k in next)) next[k] = n.acceptable === false || !!n.erreursAutomate;
        }
      }
      return next;
    });
    // Motif du refus PRÉ-REMPLI depuis ce que l'automate a noté — modifiable,
    // jamais imposé sans recours.
    setPointsMotifs((m) => {
      const next = { ...m };
      for (const l of d.reconnues) {
        const k0 = `${l.analyteCode}#${l.numeroTentative}`;
        for (const n of l.niveaux) {
          const k = `${k0}#${n.niveau}`;
          if (!(k in next) && n.erreursAutomate) next[k] = `Anomalie notée par l’automate : ${n.erreursAutomate}.`;
        }
      }
      return next;
    });
    // Motif de la REPASSE pré-rempli sur « Linéarité de la courbe » quand
    // l'automate l'a lui-même noté sur l'une des tentatives — l'opérateur
    // garde la main, mais n'a pas à retaper ce que le rapport dit déjà.
    setMotifsRepasse((m) => {
      const next = { ...m };
      for (const l of d.reconnues) {
        const k = `${l.analyteCode}#${l.numeroTentative}`;
        if (!(k in next) && l.niveaux.some((n) => /lin[ée]arit/i.test(n.erreursAutomate || ''))) next[k] = 'linearite_courbe';
      }
      return next;
    });
  };

  const appelerApercu = () => run('cqi-import-apercu', async () => {
    setErreur('');
    if (!equipementCode) { setErreur('Choisissez l’équipement — jamais déduit du fichier.'); return; }
    if (!lotControle) { setErreur('Choisissez le lot de contrôle — avant l’aperçu, pas après.'); return; }
    if (!fichierB64) { setErreur('Choisissez un fichier PDF.'); return; }
    const corps = Object.entries(corrections).map(([k, c]) => {
      const [page, ligneIndex] = k.split(':').map(Number);
      return { page, ligneIndex, ...c };
    });
    const r = await fetchEcriture('/api/ged/ged/cqi-import/apercu', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ equipementCode, lotControle, fichierB64, corrections: corps }),
    });
    if (!r.ok) {
      setErreur((await r.json().catch(() => ({}))).error?.message || 'Lecture du fichier refusée.');
      return;
    }
    const d: ReponseApercu = await r.json();
    appliquerReponse(d);
    if (!date) setDate(d.dateRapport);
    if (!operateur) setOperateur(d.operateurPropose);
  });

  const confirmer = () => run('cqi-import-valider', async () => {
    if (!apercu) return;
    const gardeesL = apercu.reconnues.filter((l) => gardees[`${l.analyteCode}#${l.numeroTentative}`]);
    const lignes = gardeesL.map((l) => {
      const attemptKey = `${l.analyteCode}#${l.numeroTentative}`;
      return {
        analyteCode: l.analyteCode, periodeId: l.periodeId,
        numeroTentative: l.numeroTentative, motifRepasse: motifsRepasse[attemptKey] || undefined,
        /* Ce que la revue a recueilli sur CE paramètre. Le lot propre l'emporte
           sur celui de la série ; l'observation est jointe au commentaire du
           passage — c'est là qu'on la relira dans six mois. */
        lotReactif: (lotsParLigne[attemptKey] || '').trim() || undefined,
        observation: (notesParLigne[attemptKey] || '').trim() || undefined,
        /* Le doublon que l'aperçu a MONTRÉ et que l'opérateur garde sciemment (14/10/2026) :
           le serveur recherche les doublons à l'écriture et n'écarte que ceux qu'on n'a pas vus. */
        doublonAccepte: l.doublon?.passageId || undefined,
        valeurs: l.niveaux.map((n) => {
          const kp = `${attemptKey}#${n.niveau}`;
          const rejete = !!pointsRejetes[kp];
          return {
            niveau: n.niveau, valeur: n.valeur,
            rejete, rejeteMotif: rejete ? (pointsMotifs[kp] || '').trim() : undefined,
          };
        }),
      };
    });
    if (!lignes.length) { setErreur('Aucun point coché à enregistrer.'); return; }
    const r = await fetchEcriture('/api/ged/ged/cqi-import/valider', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        equipementCode, lotControle, date, operateur, lignes,
        lotReactif: lotReactif.trim() || undefined,
        versionLogicielFichier: apercu.versionLogicielFichier || undefined,
        // Un responsable déjà habilité agit sans ce champ (son PROPRE profil
        // suffit, revérifié côté serveur) ; sinon c'est lui qui l'authentifie.
        responsable: apercu.alerteMachine ? authResponsableMachine.payload : undefined,
      }),
    });
    if (!r.ok) {
      setErreur((await r.json().catch(() => ({}))).error?.message || 'Enregistrement refusé.');
      return;
    }
    const res = await r.json();
    /* Les lignes ÉCARTÉES se disent (14/10/2026) — dont les doublons apparus depuis l'aperçu
       (une saisie manuelle du même contrôle entre-temps) : sans cela, « 12 importés » sur
       14 cochés se lisait comme un succès complet. */
    const ecartees = Array.isArray(res.ignorees) ? res.ignorees as { analyteCode: string; motif: string }[] : [];
    const doublons = ecartees.filter((x) => x.motif === 'doublon');
    const compte = `${res.enregistres} point(s) CQI importé(s)`
      + (res.rejets ? ` · ${res.rejets} en rejet` : '')
      + (res.alertes ? ` · ${res.alertes} en avertissement` : '')
      + (ecartees.length ? ` · ${ecartees.length} non écrit(s)${doublons.length ? `, dont ${doublons.length} déjà saisi(s) entre-temps (${doublons.map((x) => x.analyteCode).join(', ')})` : ''}` : '') + '.';
    showToast(compte, res.rejets ? 'erreur' : 'info');
    fermer();
    onImporte();
  });

  // Une ligne PAR NIVEAU — jamais deux valeurs empilées dans la même cellule.
  const lignesReconnues = useMemo<LigneAffichee[]>(() => {
    if (!apercu) return [];
    const nbTentatives = new Map<string, number>();
    for (const l of apercu.reconnues) nbTentatives.set(l.analyteCode, (nbTentatives.get(l.analyteCode) || 0) + 1);
    const lignes = apercu.reconnues.flatMap((l) => l.niveaux.map((n) => ({
      attemptKey: `${l.analyteCode}#${l.numeroTentative}`,
      analyteCode: l.analyteCode, analyteNom: l.analyteNom, numeroTentative: l.numeroTentative,
      nbTentativesAnalyte: nbTentatives.get(l.analyteCode) || 1,
      reglesPrevu: l.reglesPrevu,
      niveau: n.niveau, valeur: n.valeur, z: n.z, acceptable: n.acceptable, unite: n.unite,
      corrige: n.corrige, erreursAutomate: n.erreursAutomate || '',
    })));
    return vue === 'niveau'
      ? [...lignes].sort((a, b) => a.niveau - b.niveau || a.analyteNom.localeCompare(b.analyteNom) || a.numeroTentative - b.numeroTentative)
      : lignes;
  }, [apercu, vue]);

  /** Sévérité d'une ligne, pour trier la colonne « Verdict » — même hiérarchie
   *  que `couleurCase` (une règle déclenchée prime sur le seul z), traduite en
   *  un rang comparable plutôt qu'en couleur. */
  const graviteDe = (l: LigneAffichee): number => {
    if (l.acceptable === null) return -1;
    const regle = l.reglesPrevu.find((r) => r.niveau === null || r.niveau === l.niveau);
    const grave = regle?.gravite === 'rejet' || (!regle && l.acceptable === false && Math.abs(l.z ?? 0) >= 3);
    const alerte = regle?.gravite === 'alerte' || (!regle && Math.abs(l.z ?? 0) >= 2);
    return grave ? 2 : alerte ? 1 : 0;
  };

  /** La PREMIÈRE ligne d'une tentative — celle qui porte « Carte », le doublon
   *  et le motif de repasse — est son niveau le plus bas : une définition qui
   *  ne dépend pas de l'ordre d'affichage, que le tri du tableau change. */
  const premierNiveauParAttempt = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lignesReconnues) m.set(l.attemptKey, Math.min(m.get(l.attemptKey) ?? Infinity, l.niveau));
    return m;
  }, [lignesReconnues]);
  const premiereDuGroupe = (l: LigneAffichee) => premierNiveauParAttempt.get(l.attemptKey) === l.niveau;

  /** Carte/lot par tentative — repris de `apercu.reconnues`, jamais recalculé :
   *  c'est la SEULE source pour le lot en vigueur d'une ligne. */
  const infosParAttempt = useMemo(() => {
    const m = new Map<string, { carteId: string; analyteNom: string; lotControle: string; doublon: LigneReconnue['doublon'] }>();
    if (apercu) for (const l of apercu.reconnues) {
      m.set(`${l.analyteCode}#${l.numeroTentative}`, {
        carteId: l.carteId, analyteNom: l.analyteNom, lotControle: l.lotControle, doublon: l.doublon ?? null,
      });
    }
    return m;
  }, [apercu]);

  /** Tentatives GARDÉES par analyte — la même règle que le serveur applique
   *  pour exiger un motif de repasse : compter parmi les lignes ENVOYÉES, pas
   *  parmi tout ce que le rapport contenait. */
  const nbGardeesParAnalyte = useMemo(() => {
    const m = new Map<string, number>();
    if (apercu) for (const l of apercu.reconnues) {
      if (gardees[`${l.analyteCode}#${l.numeroTentative}`]) m.set(l.analyteCode, (m.get(l.analyteCode) || 0) + 1);
    }
    return m;
  }, [apercu, gardees]);

  /** Attentes bloquant la confirmation, au-delà du recalcul d'aperçu :
   *  chaque repasse motivée, chaque point refusé motivé (≥ 5 caractères —
   *  même règle que le rejet a posteriori). Le lot, lui, est déjà tranché
   *  AVANT l'aperçu — rien à revérifier ici. */
  /* CE QUE LA DÉTECTION A TROUVÉ — compté sur ce que le rapport contient, et
     non sur ce qui sera gardé : c'est la lecture du FICHIER qu'on valide à
     l'étape 1. Un point = un niveau mesuré ; un passage = une tentative. */
  const compteDetection = useMemo(() => {
    if (!apercu) return { points: 0, analytes: 0, tentatives: 0, doublons: 0 };
    return {
      points: apercu.reconnues.reduce((n, l) => n + l.niveaux.length, 0),
      analytes: new Set(apercu.reconnues.map((l) => l.analyteCode)).size,
      tentatives: apercu.reconnues.length,
      doublons: apercu.reconnues.filter((l) => l.doublon).length,
    };
  }, [apercu]);

  /* CE QUI PASSE EN REVUE — les lignes GARDÉES, dans l'ordre du rapport.
     ⚠️ Décocher à l'étape 1 retire de la revue : on ne fait pas regarder une
     courbe pour un point qu'on vient d'écarter. */
  const lignesARevoir = useMemo(
    () => (apercu?.reconnues || []).filter((l) => gardees[`${l.analyteCode}#${l.numeroTentative}`]),
    [apercu, gardees],
  );

  const attentes = useMemo(() => {
    if (!apercu) return { ok: false, motifsManquants: 0, rejetsSansMotif: 0 };
    let motifsManquants = 0;
    let rejetsSansMotif = 0;
    for (const l of apercu.reconnues) {
      const attemptKey = `${l.analyteCode}#${l.numeroTentative}`;
      if (!gardees[attemptKey]) continue;
      if ((nbGardeesParAnalyte.get(l.analyteCode) || 0) > 1 && !motifsRepasse[attemptKey]) motifsManquants += 1;
      for (const n of l.niveaux) {
        const kp = `${attemptKey}#${n.niveau}`;
        if (pointsRejetes[kp] && (pointsMotifs[kp] || '').trim().length < 5) rejetsSansMotif += 1;
      }
    }
    // Alerte machine : une case à cocher pour qui a déjà le droit sur son
    // poste, l'authentification d'un responsable pour qui ne l'a pas —
    // jamais un blocage que personne ne peut lever, jamais une case ou un
    // formulaire pré-rempli qui le lèverait tout seul.
    const alerteBloquante = !!apercu.alerteMachine
      && (peutDeroger ? !forcerAlerteMachine : !authResponsableMachine.pret);
    /* Qui a fait la manip est EXIGÉ (09/09/2026) : la route le revérifie de
       toute façon, mais un bouton qui part pour revenir en erreur ne dit pas
       ce qui manque. */
    const sansOperateur = !operateur;
    return {
      ok: !motifsManquants && !rejetsSansMotif && !alerteBloquante && !sansOperateur,
      motifsManquants, rejetsSansMotif, alerteBloquante, sansOperateur,
    };
  }, [apercu, gardees, motifsRepasse, pointsRejetes, pointsMotifs, nbGardeesParAnalyte, peutDeroger, forcerAlerteMachine, authResponsableMachine.pret, operateur]);

  if (!ouvert) {
    return (
      // Libellé générique : d'autres équipements importeront depuis un
      // Excel ou un autre format, pas seulement un PDF Indiko (05/09/2026).
      <Bouton taille="sm" onClick={() => setOuvert(true)}>
        <I n="upload" s={13} />Importer
      </Bouton>
    );
  }

  return (
    <Modale titre="Importer un rapport CQI Indiko" onClose={fermer} largeur={820}>
      {!apercu && (
        <div style={{ display: 'grid', gap: 12 }}>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
            Le « Rapport de résultats du test » imprimé (ou exporté en PDF) juste après la série de
            contrôle. La date et l’opérateur pourront être corrigés à l’étape suivante — ce fichier
            ne les porte pas de façon fiable.
          </p>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>
            Équipement — à choisir avant tout, jamais déduit du fichier
            <Selecteur value={equipementCode} onChange={setEquipementCode} style={{ ...inputStyle, marginTop: 4 }}>
              <option value="">— choisir —</option>
              {automates.map((e) => <option key={e.code} value={e.code}>{e.code} · {e.nom}</option>)}
            </Selecteur>
          </label>
          {/* Le lot se choisit ICI, avant même le fichier (05/09/2026) : c'est
              lui qui dit contre quelle carte chaque point du rapport sera
              évalué — le confirmer une fois l'aperçu déjà calculé serait
              trop tard. */}
          {equipementCode && (
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>
              Lot de contrôle en vigueur — à choisir avant l’aperçu
              {lotsDisponibles === null ? (
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 400, margin: '4px 0 0' }}>Recherche des lots ouverts…</p>
              ) : lotsDisponibles.length === 0 ? (
                <p style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 400, margin: '4px 0 0' }}>
                  Aucun lot ouvert sur cet équipement — ouvrez d’abord un « Nouveau lot » sur les cartes concernées.
                </p>
              ) : (
                <select value={lotControle} onChange={(e) => setLotControle(e.target.value)} style={{ ...inputStyle, marginTop: 4 }}>
                  <option value="">— choisir —</option>
                  {lotsDisponibles.map((l) => <option key={l.lot} value={l.lot}>{l.lot} ({l.cartes} carte{l.cartes > 1 ? 's' : ''})</option>)}
                </select>
              )}
            </label>
          )}
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: (equipementCode && lotControle) ? 'var(--text-muted)' : 'var(--text-faint)' }}>
            Fichier PDF{!equipementCode
              ? <span style={{ fontWeight: 400 }}> — choisissez d’abord l’équipement</span>
              : !lotControle && <span style={{ fontWeight: 400 }}> — choisissez d’abord le lot de contrôle</span>}
            <input type="file" accept=".pdf,application/pdf" disabled={!equipementCode || !lotControle}
              style={{ ...inputStyle, width: '100%', padding: 6, marginTop: 4, opacity: (equipementCode && lotControle) ? 1 : 0.5 }}
              onChange={(e) => { void choisirFichier(e.target.files?.[0] || null); }} />
          </label>
          {nomFichier && <p style={{ fontSize: 12.5, margin: 0 }}>Fichier retenu : <strong>{nomFichier}</strong></p>}
          {erreur && <p style={{ fontSize: 12.5, color: 'var(--red-600)', margin: 0 }}>{erreur}</p>}
          <PiedActions taille="sm" annuler={{ onClick: fermer }} principale={{ libelle: 'Aperçu', libelleChargement: 'Lecture…', chargement: estOccupe('cqi-import-apercu'), disabled: !fichierB64 || estOccupe(), onClick: appelerApercu }} />
        </div>
      )}

      {apercu && etape === 'detection' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {/* ── ÉTAPE 1 : CE QUE LE FICHIER CONTIENT ─────────────────────────
              « Une étape pour valider la détection des points par
               l'importateur (y afficher le nombre de points détectés) » — RQ,
               12/09/2026.
              La question posée ici est « le fichier a-t-il été LU
              correctement », et elle se tranche en comparant au PDF — d'où son
              affichage, demandé le 11/09 (« afficher le pdf importé au moment
              de l'aperçu »). La question « ce point a-t-il sa place sur la
              carte » est tout autre : elle demande la COURBE, et c'est
              l'étape 2. */}
          <EtapesImport actuelle="detection" />
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)' }}>
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>Points détectés</div>
              <div style={{ fontSize: 21, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--text-strong)', lineHeight: 1.2 }}>{compteDetection.points}</div>
            </div>
            <span style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
              sur <strong>{compteDetection.analytes}</strong> paramètre(s)
              {compteDetection.tentatives > compteDetection.analytes
                && <> · <strong>{compteDetection.tentatives}</strong> passage(s), reprises comprises</>}
              {!!apercu.nonReconnues.length && (
                <> · <strong style={{ color: 'var(--amber-600)' }}>{apercu.nonReconnues.length} non reconnu(s)</strong></>
              )}
              {!!apercu.exclues.length && <> · {apercu.exclues.length} écarté(s)</>}
            </span>
            {/* ⚠️ LE DÉCOMPTE DES DOUBLONS EST DIT ICI, à l'étape où l'on
                compare au rapport : c'est le moment où « j'ai déjà chargé ce
                fichier » se reconnaît d'un coup d'œil, au lieu de se découvrir
                ligne à ligne plus bas. */}
            {compteDetection.doublons > 0 && (
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', flexBasis: '100%', lineHeight: 1.5 }}>
                ⚠ {compteDetection.doublons} passage(s) existent DÉJÀ à cette date, avec les mêmes valeurs
                {compteDetection.doublons === compteDetection.tentatives
                  ? ' — tout le rapport. Ce fichier a vraisemblablement déjà été importé.'
                  : ' — décochés par défaut, cochez ceux qui sont de vraies repasses.'}
              </span>
            )}
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', maxWidth: '40ch' }}>
              Comparez au rapport ci-dessous. Rien n’est enregistré à cette étape.
            </span>
          </div>
          {/* LE PDF, À CÔTÉ DU TABLEAU (retour 016 du 11/09). On validait un
              tableau sans pouvoir le confronter à sa source : le fichier était
              lu en base64, envoyé, et jamais montré. */}
          {!!fichierB64 && <ApercuFichier b64={fichierB64} nom={nomFichier} />}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>
              Date<br />
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
            </label>
            {/* QUI A FAIT LA MANIP — choisi dans l'organigramme, jamais tapé
                (09/09/2026, RQ). Deux graphies de la même personne feraient
                deux opérateurs qu'aucun journal ne rapproche. */}
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>
              Passé par<br />
              <select value={operateur} onChange={(e) => setOperateur(e.target.value)} style={inputStyle}>
                <option value="">— choisir —</option>
                {(apercu.operateursDisponibles || []).map((o) => (
                  <option key={o.key} value={o.key}>{o.nom}</option>
                ))}
              </select>
            </label>
            {/* Le lot de RÉACTIF — à ne pas confondre avec celui du CONTRÔLE,
                choisi plus haut. Deux registres : le lot de contrôle ouvre une
                carte neuve (la cible change) ; le lot de réactif se marque SUR
                la carte en cours, d'un trait vertical. */}
            <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>
              Lot de réactif <span style={{ fontWeight: 400 }}>— facultatif</span><br />
              <input value={lotReactif} onChange={(e) => setLotReactif(e.target.value)}
                placeholder="commun à la série" style={{ ...inputStyle, minWidth: 150 }} />
            </label>
            {/* QUI SAISIT — constaté, pas choisi : c'est la session ouverte.
                Affiché seulement quand il diffère, pour que la double
                attribution soit VUE avant d'être enregistrée. */}
            {operateur && operateur !== apercu.operateurPropose && (
              <div style={{ fontSize: 12.5, color: 'var(--text-body)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '6px 10px', alignSelf: 'flex-end' }}>
                Saisi par <strong>{(apercu.operateursDisponibles || []).find((o) => o.key === apercu.operateurPropose)?.nom || apercu.operateurPropose}</strong> — les deux seront enregistrés, et le journal le dira.
              </div>
            )}
            <Segment ariaLabel="Regroupement" valeur={vue} onChange={setVue} style={{ marginInlineStart: 'auto' }}
              options={[{ valeur: 'parametre' as const, libelle: 'Par paramètre' }, { valeur: 'niveau' as const, libelle: 'Par niveau' }]} />
          </div>

          {/* Sécurité de routine (06/09/2026, demande d'Inès) : la date
              saisie ci-dessus vient du texte imprimé et reste modifiable à
              la main — un vieux PDF rechargé par erreur la porterait quand
              même. `dateCreationFichier` est une métadonnée du FICHIER
              lui-même, posée par l'automate au moment de l'impression :
              indépendante du champ ci-dessus, elle ne peut pas mentir de la
              même façon. Un écart n'est pas bloqué (une saisie tardive
              légitime existe) mais reste affiché tant qu'il persiste. */}
          {apercu.dateCreationFichier && apercu.dateCreationFichier !== date && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--red-50)', border: '1px solid var(--red-200)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', fontSize: 12.5 }}>
              <I n="alert" s={14} />
              <strong style={{ color: 'var(--red-600)' }}>
                Ce fichier a été créé le {fmtDate(apercu.dateCreationFichier)}, différent de la date saisie ({fmtDate(date)}).
              </strong>
              <span style={{ color: 'var(--text-body)' }}>Vérifiez qu’il s’agit bien du rapport du jour avant de confirmer.</span>
            </div>
          )}

          {/* Garde-fou machine (06/09/2026, demande d'Inès) : « Version du
              logiciel » imprimée par CET automate diverge de celle attendue
              pour l'équipement choisi — le fichier vient vraisemblablement
              de l'AUTRE Indiko malgré le bon choix à l'écran. NON DÉROGEABLE
              pour qui n'a pas le droit de dérogation CQI : pas de case à
              cocher, pas de bouton, le mur reste entier. Pour un responsable
              (`peutDeroger`), une case explicite lève le blocage — jamais
              pré-cochée, et l'enregistrement en garde la trace au journal. */}
          {/* CE QUE LE FICHIER EST (09/09/2026, RQ : « un pdf provenant des
              archives […] n'est pas reconnu »). Les deux gabarits sont lus,
              mais ils ne se valent pas : l'archivage date ses points ligne par
              ligne et contient des patients, qui sont comptés sans jamais être
              lus. Le dire ici, c'est permettre de voir tout de suite qu'on a
              chargé l'un pour l'autre. */}
          <div style={{ display: 'grid', gap: 3, background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', fontSize: 12.5 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong>{apercu.gabarit === 'archivage' ? 'Rapport d’archivage' : 'Rapport du jour'}</strong>
              <span style={{ color: 'var(--text-body)' }}>
                {apercu.gabarit === 'archivage' ? (
                  <>Passage du <strong>{fmtDate(apercu.dateRapport)}</strong> — la date portée par les lignes de contrôle, et non celle d’impression du rapport.</>
                ) : (
                  <>Passage du <strong>{fmtDate(apercu.dateRapport)}</strong>.</>
                )}
              </span>
            </div>
            {/* CE QUE LE FICHIER DIT DE LUI-MÊME, nommé pour ce que c'est :
                l'heure imprimée en tête est celle de l'ÉDITION du rapport, pas
                celle du passage — l'automate n'imprime pas l'heure de passage,
                ni sur le rapport du jour ni sur l'archivage (vérifié le
                09/09/2026). L'afficher comme « heure du CQI » ferait dater un
                passage du matin de 11 h 57 parce que le rapport a été tiré à
                cette heure-là. */}
            <div style={{ color: 'var(--text-muted)' }}>
              Rapport édité le {fmtDate(apercu.dateCreationFichier || apercu.dateRapport)}
              {apercu.heureRapport ? ` à ${apercu.heureRapport}` : ''} — l’automate n’imprime pas l’heure de chaque passage.
            </div>
          </div>

          {apercu.alerteMachine && (
            <div style={{ display: 'grid', gap: 6, background: 'var(--red-50)', border: '2px solid var(--red-300)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', fontSize: 12.5 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <I n="alert" s={16} />
                <strong style={{ color: 'var(--red-600)' }}>
                  Ce fichier porte la version logicielle {apercu.alerteMachine.trouvee}, attendue {apercu.alerteMachine.attendue} pour {equipementCode}.
                </strong>
              </div>
              <span style={{ color: 'var(--text-body)' }}>
                Ce PDF vient vraisemblablement de l’AUTRE automate — vérifiez l’équipement choisi avant de continuer.
              </span>
              {peutDeroger ? (
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 700, color: 'var(--red-700)' }}>
                  <input type="checkbox" checked={forcerAlerteMachine} onChange={(e) => setForcerAlerteMachine(e.target.checked)} />
                  Je confirme malgré l’alerte, en tant que responsable — l’enregistrement en gardera la trace.
                </label>
              ) : (
                <ChampsResponsable {...authResponsableMachine} libelle="cet import malgré l’alerte" />
              )}
            </div>
          )}

          {/* Rappel, pas une confirmation : le lot a déjà été choisi et
              vérifié carte par carte AVANT ce calcul (voir plus haut). */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', fontSize: 12.5 }}>
            <I n="tube" s={14} />
            <span style={{ color: 'var(--text-muted)' }}>Lot de contrôle :</span>
            <strong style={{ fontFamily: 'var(--font-mono)' }}>{lotControle}</strong>
          </div>

          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>
              Points reconnus ({lignesReconnues.length})
            </div>
            {/* L7 vague 2 (11/10/2026) : l'aperçu passe sur le socle au niveau SIMPLE —
                le tri maison (`basculerTri`, dette nommée dans `qualite-tri-unique`)
                tombe ; le regroupement « par paramètre » / « par niveau » (`vue`) reste
                l'ordre de base tant que personne ne trie.
                ⚠️ « Carte », doublon et motif de repasse se posent sur la PREMIÈRE
                ligne de la tentative, définie par son niveau le plus bas — et non
                plus par l'ordre d'affichage, que le tri change. */}
            <Tableau
              id="cqi-import-points"
              niveau="simple"
              lignes={lignesReconnues}
              cleLigne={(l) => `${l.attemptKey}#${l.niveau}`}
              vide={{ titre: 'Aucun point CQI reconnu dans ce fichier.' }}
              colonnes={[
                {
                  cle: 'garder', libelle: 'Garder', style: { width: 56 },
                  rendu: (l) => (
                    <input type="checkbox" checked={!!gardees[l.attemptKey]} aria-label={`Garder ${l.analyteNom}, tentative ${l.numeroTentative}`}
                      onChange={(e) => setGardees((g) => ({ ...g, [l.attemptKey]: e.target.checked }))} />
                  ),
                },
                {
                  cle: 'parametre', libelle: 'Paramètre', valeur: (l) => l.analyteNom, style: { fontWeight: 700 },
                  rendu: (l) => {
                    const infos = infosParAttempt.get(l.attemptKey);
                    const premiere = premiereDuGroupe(l);
                    return (
                      <>
                        {l.analyteNom}
                        {l.nbTentativesAnalyte > 1 && (
                          <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · tentative {l.numeroTentative}</span>
                        )}
                        {l.corrige && <span title="Ligne corrigée manuellement" style={{ color: 'var(--cyan-700)' }}> ✎</span>}
                        {premiere && infos?.carteId && (
                          <Bouton taille="sm" title="Voir la carte et le graphe (lecture seule)" onClick={() => setCarteOuverte({ carteId: infos.carteId, analyteNom: l.analyteNom })} style={{ marginInlineStart: 6 }}>
                            <I n="chart" s={11} />Carte
                          </Bouton>
                        )}
                        {/* ⚠️ DÉJÀ EN BASE (12/09/2026) — « un point ne doit
                            pas être importé deux fois dans la courbe »
                            (Manel, 11/09). La tentative est proposée
                            DÉCOCHÉE ; on dit ce qui a été trouvé, et on
                            laisse trancher : une vraie repasse dont les
                            valeurs se trouvent identiques existe. */}
                        {premiere && infos?.doublon && (
                          <div style={{ fontWeight: 400, fontSize: 11.5, color: 'var(--amber-600)', marginTop: 2, lineHeight: 1.45 }}>
                            ⚠ Ce passage existe déjà à cette date
                            {infos.doublon.serie ? ` (série n°${infos.doublon.serie}` : ' ('}
                            {infos.doublon.importe ? ', importé' : ', saisi à la main'}
                            {infos.doublon.operateur ? ` par ${userName(settings, infos.doublon.operateur)}` : ''}
                            ) — mêmes valeurs à tous les niveaux. Décoché par défaut.
                          </div>
                        )}
                      </>
                    );
                  },
                },
                { cle: 'niveau', libelle: 'Niveau', valeur: (l) => l.niveau, style: { fontFamily: 'var(--font-mono)' }, rendu: (l) => `N${l.niveau}` },
                {
                  cle: 'valeur', libelle: 'Valeur', type: 'nombre', valeur: (l) => l.valeur,
                  /* Lecture seule, volontairement : ce que l'automate a mesuré ne
                     se réécrit pas dans une case. Seule l'IDENTITÉ (niveau, test)
                     se corrige, dans la section « non reconnu » ci-dessous. */
                  rendu: (l) => {
                    const { fond, texte } = couleurCase(l);
                    const regles = l.reglesPrevu.filter((r) => r.niveau === null || r.niveau === l.niveau);
                    const titre = l.z !== null
                      ? `z = ${l.z >= 0 ? '+' : ''}${l.z.toFixed(2)}σ${regles.length ? ` — ${regles.map((r) => r.code).join(', ')}` : ''}`
                      : 'Aucune cible exploitable pour ce niveau.';
                    return (
                      <span title={titre} style={{ display: 'inline-block', minWidth: 70, padding: '2px 8px', borderRadius: 'var(--radius-sm)', background: fond, color: texte, fontWeight: 700, fontFamily: 'var(--font-mono)', cursor: 'help' }}>
                        {l.valeur} {l.unite}
                      </span>
                    );
                  },
                },
                {
                  cle: 'repasse', libelle: 'Repasse',
                  rendu: (l) => (premiereDuGroupe(l) && l.nbTentativesAnalyte > 1 ? (
                    <select value={motifsRepasse[l.attemptKey] || ''} style={{ ...inputStyle, fontSize: 11.5, padding: '2px 4px', width: 190 }}
                      aria-label={`Motif de la repasse de ${l.analyteNom}, tentative ${l.numeroTentative}`}
                      onChange={(e) => {
                        const motif = e.target.value;
                        setMotifsRepasse((m) => ({ ...m, [l.attemptKey]: motif }));
                        if (MOTIFS_IMPLIQUANT_CAL.has(motif)) {
                          setCalibrationOuverte({
                            equipementCode, analyteLibelle: l.analyteNom, analyteCode: l.analyteCode,
                            serie: `${date || 'import en cours'} — import PDF (${MOTIFS_REPASSE[motif]})`, regle: '',
                          });
                        }
                      }}>
                      <option value="">— motif de la repasse —</option>
                      {Object.entries(MOTIFS_REPASSE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  ) : null),
                },
                {
                  cle: 'verdict', libelle: 'Verdict', valeur: (l) => graviteDe(l),
                  rendu: (l) => {
                    const kp = `${l.attemptKey}#${l.niveau}`;
                    const rejete = !!pointsRejetes[kp];
                    return (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Bouton variante={!rejete ? 'primaire' : 'contour'} taille="sm" title="Accepter ce point" onClick={() => setPointsRejetes((p) => ({ ...p, [kp]: false }))}>✓</Bouton>
                        <Bouton variante={rejete ? 'primaire' : 'contour'} taille="sm" title="Refuser ce point (motif requis)" onClick={() => setPointsRejetes((p) => ({ ...p, [kp]: true }))}>✕</Bouton>
                        {rejete && (
                          <input type="text" placeholder="motif du refus (5 car. min.)" value={pointsMotifs[kp] || ''}
                            aria-label={`Motif du refus de ${l.analyteNom} N${l.niveau}`}
                            onChange={(e) => setPointsMotifs((m) => ({ ...m, [kp]: e.target.value }))}
                            style={{ ...inputStyle, fontSize: 11, padding: '2px 4px', width: 150 }} />
                        )}
                      </div>
                    );
                  },
                },
              ]}
            />
          </div>

          {(() => {
            const bannis = apercu.nonReconnues.filter((n) => n.motif === 'controle_banni');
            const autres = apercu.nonReconnues.filter((n) => n.motif !== 'controle_banni');
            return (
              <>
                {/* REFUSÉ, pas seulement « non reconnu » : ces lignes sont bien
                    des points CQI, saisis sous une graphie que le laboratoire a
                    décidé d'abandonner — toujours visible, jamais repliée, pour
                    que la correction à faire au poste de saisie saute aux yeux.
                    Pas de correction inline ici, volontairement : la reprendre
                    à chaque import viderait le bannissement de son sens — la
                    correction se fait au poste de saisie, une fois pour toutes. */}
                {bannis.length > 0 && (
                  <div style={{ background: 'var(--red-50)', border: '1px solid var(--red-200)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
                    <strong style={{ fontSize: 12.5, color: 'var(--red-600)' }}>
                      Refusé ({bannis.length}) — graphie abandonnée
                    </strong>
                    <ul style={{ fontSize: 12.5, margin: '4px 0 0', paddingInlineStart: 18 }}>
                      {bannis.map((n, i) => (
                        <li key={i}>
                          {n.test || '?'} / <strong>{n.ctrlId || '—'}</strong>{n.resultat ? ` = ${n.resultat}` : ''}
                          {n.motifDetail ? ` — ${n.motifDetail}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {autres.length > 0 && (
                  <details open>
                    <summary style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', cursor: 'pointer' }}>
                      Non reconnu ({autres.length}) — corrigez ci-dessous pour faire coller au référentiel, ou laissez de côté
                    </summary>
                    <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                      {autres.map((n, i) => {
                        const corrigeable = n.page !== undefined && n.ligneIndex !== undefined;
                        const c = corrigeable ? corrections[cle(n.page as number, n.ligneIndex as number)] : undefined;
                        return (
                          <div key={i} style={{ fontSize: 12.5, padding: '6px 8px', background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span>
                              {n.test || n.analyteCode || '?'} / <strong>{n.ctrlId || '—'}</strong>{n.resultat ? ` = ${n.resultat}` : ''}
                            </span>
                            <span style={{ color: 'var(--text-muted)' }}>{MOTIFS[n.motif] || n.motif}</span>
                            {corrigeable && (
                              <>
                                <SelecteurNiveau page={n.page as number} ligneIndex={n.ligneIndex as number} valeur={c?.niveau}
                                  options={apercu.niveauxDisponibles} onChoisir={majCorrection} />
                                <SelecteurAnalyte page={n.page as number} ligneIndex={n.ligneIndex as number} valeur={c?.analyteCode}
                                  options={apercu.analytesDisponibles} onChoisir={majCorrection} />
                                {n.motif === 'valeur_illisible' && (
                                  <input type="text" inputMode="decimal" placeholder="valeur corrigée"
                                    defaultValue={n.resultat}
                                    onBlur={(e) => {
                                      const v = parseFloat(e.target.value.replace(',', '.'));
                                      if (Number.isFinite(v)) majCorrection(n.page as number, n.ligneIndex as number, { valeur: v });
                                    }}
                                    style={{ ...inputStyle, width: 90, padding: '2px 4px', fontSize: 11.5 }} />
                                )}
                                {c && Object.keys(c).length > 0 && <span style={{ color: 'var(--cyan-700)', fontWeight: 700 }}>✎ à recalculer</span>}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}
              </>
            );
          })()}

          {apercu.exclues.length > 0 && (
            <details>
              <summary style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', cursor: 'pointer' }}>
                Exclus ({apercu.exclues.length}) — calibration, pas CQI
              </summary>
              <ul style={{ fontSize: 12.5, margin: '6px 0 0', paddingInlineStart: 18 }}>
                {apercu.exclues.map((x, i) => <li key={i}>{x.test} / {x.ctrlId} = {x.resultat}</li>)}
              </ul>
            </details>
          )}

          {erreur && <p style={{ fontSize: 12.5, color: 'var(--red-600)', margin: 0 }}>{erreur}</p>}
          {dirty && (
            <p style={{ fontSize: 12.5, color: 'var(--cyan-700)', margin: 0, fontWeight: 600 }}>
              Des corrections sont en attente — recalculez l’aperçu pour voir le verdict réel avant de confirmer.
            </p>
          )}
          {!dirty && attentes.motifsManquants > 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0, fontWeight: 600 }}>
              {attentes.motifsManquants} repasse(s) sans motif — choisissez pourquoi le point a été repassé.
            </p>
          )}
          {!dirty && attentes.rejetsSansMotif > 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0, fontWeight: 600 }}>
              {attentes.rejetsSansMotif} point(s) refusé(s) sans motif suffisant (5 caractères minimum).
            </p>
          )}
          {!dirty && attentes.sansOperateur && (
            <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0, fontWeight: 600 }}>
              Choisissez qui a passé les contrôles — « Passé par », en haut.
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'end' }}>
            <Bouton onClick={() => { setApercu(null); setCorrections({}); setDirty(false); }}>Retour</Bouton>
            {dirty ? (
              <Bouton variante="primaire" disabled={estOccupe()} onClick={appelerApercu}>
                {estOccupe('cqi-import-apercu') ? 'Recalcul…' : 'Recalculer l’aperçu'}
              </Bouton>
            ) : (
              /* ⚠️ ON NE CONFIRME PLUS DEPUIS ICI. La détection dit ce que le
                 fichier contient ; elle ne dit pas si les points ont leur place
                 sur la carte. Enregistrer sans avoir regardé la courbe, c'est
                 précisément ce que la revue existe pour empêcher. */
              <Bouton variante="primaire" disabled={!lignesARevoir.length || estOccupe()} onClick={() => { setIRevue(0); setEtape('revue'); }}>
                Passer à la revue ({lignesARevoir.length} paramètre{lignesARevoir.length > 1 ? 's' : ''}) →
              </Bouton>
            )}
          </div>
        </div>
      )}

      {apercu && etape === 'revue' && (
        <RevueSequentielle
          lignes={lignesARevoir} iCourant={iRevue} onI={setIRevue} date={date}
          nbGardeesParAnalyte={nbGardeesParAnalyte}
          pointsRejetes={pointsRejetes} setPointsRejetes={setPointsRejetes}
          pointsMotifs={pointsMotifs} setPointsMotifs={setPointsMotifs}
          motifsRepasse={motifsRepasse} setMotifsRepasse={setMotifsRepasse}
          lotsParLigne={lotsParLigne} setLotsParLigne={setLotsParLigne}
          notesParLigne={notesParLigne} setNotesParLigne={setNotesParLigne}
          onCalibration={(l) => setCalibrationOuverte({
            equipementCode, analyteLibelle: l.analyteNom, analyteCode: l.analyteCode,
            serie: date, regle: l.reglesPrevu.map((r) => r.code).join(', '),
          })}
          attentes={attentes} occupe={estOccupe()} enCours={estOccupe('cqi-import-valider')}
          onRetour={() => setEtape('detection')} onConfirmer={confirmer} />
      )}
      {carteOuverte && (
        <CarteApercuModale carteId={carteOuverte.carteId} analyteNom={carteOuverte.analyteNom} onClose={() => setCarteOuverte(null)} />
      )}
      {calibrationOuverte && (
        <CalibrationDepuisCqi contexte={calibrationOuverte} exigerTracabilite
          onFerme={() => setCalibrationOuverte(null)}
          onFaite={() => setCalibrationOuverte(null)} />
      )}
    </Modale>
  );
}
